import React, { useState } from 'react';
import {
  Settings2, Trash2, Lock, Unlock, Maximize2, Minimize2, Save, Download, FileText, Printer,
  Undo2, Redo2, Grid3x3, Copy, Layers, Users, X, ChevronLeft, MoreHorizontal, PenTool,
} from 'lucide-react';
import { SPORT_ICONS, PlayerIcon, ConeIcon, ArrowIcon, ZoneAreaIcon } from './SportIcons';

export const TOKEN_TYPES = [
  { type: 'player_a', label: 'لاعب فريق 1', shortLabel: 'لاعب 1', color: '#2563eb', shape: 'circle', category: 'team', team: 'a' },
  { type: 'player_b', label: 'لاعب فريق 2', shortLabel: 'لاعب 2', color: '#dc2626', shape: 'circle', category: 'team', team: 'b' },
  { type: 'goalkeeper', label: 'حارس مرمى', shortLabel: 'حارس', color: '#16a34a', shape: 'circle', category: 'team' },
  { type: 'neutral', label: 'لاعب محايد', shortLabel: 'محايد', color: '#6b7280', shape: 'circle', category: 'team' },
  { type: 'coach', label: 'مدرب', shortLabel: 'مدرب', color: '#0f172a', shape: 'square', category: 'team' },
  { type: 'ball', label: 'كرة', shortLabel: 'كرة', color: '#f8fafc', shape: 'ball', category: 'equipment' },
  { type: 'cone', label: 'قمع', shortLabel: 'قمع', color: '#f97316', shape: 'triangle', category: 'equipment' },
  { type: 'hurdle', label: 'حاجز', shortLabel: 'حاجز', color: '#eab308', shape: 'bar', category: 'equipment' },
  { type: 'pole', label: 'عمود', shortLabel: 'عمود', color: '#94a3b8', shape: 'pole', category: 'equipment' },
  { type: 'ladder', label: 'سلم رشاقة', shortLabel: 'سلم', color: '#a16207', shape: 'ladder', category: 'equipment' },
  { type: 'hoop', label: 'طوق', shortLabel: 'طوق', color: '#db2777', shape: 'ring', category: 'equipment' },
  { type: 'mini_goal', label: 'شباك صغير', shortLabel: 'شباك', color: '#475569', shape: 'minigoal', category: 'equipment' },
  { type: 'goal', label: 'هدف كبير', shortLabel: 'مرمى', color: '#1e293b', shape: 'goal', category: 'equipment' },
  { type: 'dummy', label: 'دمية تدريب', shortLabel: 'دمية', color: '#7c3aed', shape: 'dummy', category: 'equipment' },
  { type: 'zone', label: 'منطقة ملونة', shortLabel: 'منطقة', color: '#22c55e', shape: 'zoneToken', category: 'zones' },
  { type: 'text', label: 'نص', shortLabel: 'نص', color: '#0f172a', shape: 'text', category: 'zones' },
  { type: 'number', label: 'رقم', shortLabel: 'رقم', color: '#0f172a', shape: 'number', category: 'zones' },
];

export const PATH_TOOLS = [
  { type: 'arrow', label: 'سهم مستقيم', shortLabel: 'سهم', category: 'draw' },
  { type: 'curved_arrow', label: 'سهم منحني', shortLabel: 'سهم منحني', category: 'draw' },
  { type: 'line', label: 'خط متصل', shortLabel: 'خط', category: 'draw' },
  { type: 'dashed_line', label: 'خط متقطع', shortLabel: 'خط متقطع', category: 'draw' },
  { type: 'freehand', label: 'رسم حر', shortLabel: 'رسم حر', category: 'draw' },
  { type: 'movement_path', label: 'مسار حركة (بلا كرة)', shortLabel: 'مسار حركة', category: 'draw' },
  { type: 'pass_path', label: 'مسار تمرير الكرة', shortLabel: 'تمريرة', category: 'draw' },
  { type: 'dribble_path', label: 'مسار جري بالكرة', shortLabel: 'جري بالكرة', category: 'draw' },
  { type: 'circle', label: 'دائرة', shortLabel: 'دائرة', category: 'zones' },
  { type: 'rect', label: 'مستطيل', shortLabel: 'مستطيل', category: 'zones' },
  { type: 'zone_area', label: 'منطقة شفافة', shortLabel: 'منطقة شفافة', category: 'zones' },
];

export const FIELD_BACKGROUNDS = [
  { key: 'green', label: 'عشب', fill: '#2f8f46', stripe: '#34994c', line: '#ffffff' },
  { key: 'dark', label: 'غامق', fill: '#141c29', stripe: '#19222f', line: '#dbe4ef' },
  { key: 'light', label: 'فاتح', fill: '#e8f3ea', stripe: '#eef7f0', line: '#16553a' },
  { key: 'white', label: 'أبيض', fill: '#ffffff', line: '#1f2937', grass: false },
  { key: 'blue', label: 'صالة', fill: '#1d6fa5', stripe: '#2179b0', line: '#ffffff' },
  { key: 'clay', label: 'ترابي', fill: '#a9743f', stripe: '#b07c46', line: '#fff6e8' },
];

const COLORS = ['#0f172a', '#dc2626', '#2563eb', '#16a34a', '#f97316', '#a855f7', '#ffffff'];
const DRAG_DRAW_TYPES = ['arrow', 'curved_arrow', 'line', 'dashed_line', 'freehand', 'movement_path', 'pass_path', 'dribble_path'];

const CATEGORIES = [
  { key: 'team', label: 'اللاعبون', icon: PlayerIcon },
  { key: 'equipment', label: 'المعدات', icon: ConeIcon },
  { key: 'draw', label: 'الحركة والتمرير', icon: ArrowIcon },
  { key: 'zones', label: 'المناطق', icon: ZoneAreaIcon },
];

const BarBtn = ({ onClick, disabled, active, testId, title, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testId}
    className={`flex h-10 w-10 items-center justify-center rounded-xl transition-all active:scale-90 disabled:opacity-30 ${
      active ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30' : 'text-slate-300 hover:bg-white/10'
    }`}
  >
    {children}
  </button>
);

/* ---------------- TOP BAR ---------------- */
export function PitchToolbarTop({
  readOnly, onDelete, hasSelection, onLock, isLocked, fullscreen, onToggleFullscreen,
  onSave, saveStatus, onExportPng, onExportPdf, onPrint, onOpenSettings, onAutoFormation,
}) {
  const [exportOpen, setExportOpen] = useState(false);
  return (
    <div className="flex items-center justify-between gap-1 bg-[#0B0E14] px-2 py-2" data-testid="pitch-toolbar-top">
      <div className="flex items-center gap-1">
        <BarBtn onClick={onOpenSettings} testId="pitch-settings-btn" title="إعدادات الملعب"><Settings2 className="h-[18px] w-[18px]" /></BarBtn>
        {!readOnly && (
          <>
            <BarBtn onClick={onAutoFormation} testId="pitch-auto-formation-btn" title="توزيع تلقائي (4-3-3)"><Users className="h-[18px] w-[18px]" /></BarBtn>
            <BarBtn onClick={onDelete} disabled={!hasSelection} testId="pitch-delete-btn" title="حذف"><Trash2 className="h-[18px] w-[18px]" /></BarBtn>
            <BarBtn onClick={onLock} disabled={!hasSelection} active={isLocked} testId="pitch-lock-btn" title="قفل/فتح">
              {isLocked ? <Lock className="h-[18px] w-[18px]" /> : <Unlock className="h-[18px] w-[18px]" />}
            </BarBtn>
          </>
        )}
      </div>
      <div className="flex items-center gap-1">
        <BarBtn onClick={onToggleFullscreen} testId="pitch-fullscreen-btn" title={fullscreen ? 'إنهاء ملء الشاشة' : 'ملء الشاشة'}>
          {fullscreen ? <Minimize2 className="h-[18px] w-[18px]" /> : <Maximize2 className="h-[18px] w-[18px]" />}
        </BarBtn>
        <div className="relative">
          <BarBtn onClick={() => setExportOpen((o) => !o)} testId="pitch-export-menu-btn" title="تصدير"><Download className="h-[18px] w-[18px]" /></BarBtn>
          {exportOpen && (
            <div className="absolute left-0 top-11 z-30 w-40 space-y-0.5 rounded-xl border border-white/10 bg-[#151b26] p-1.5 shadow-2xl">
              <button onClick={() => { onExportPng(); setExportOpen(false); }} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-semibold text-slate-200 hover:bg-white/10" data-testid="pitch-export-png-btn"><Download className="h-3.5 w-3.5" /> صورة PNG</button>
              <button onClick={() => { onExportPdf(); setExportOpen(false); }} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-semibold text-slate-200 hover:bg-white/10" data-testid="pitch-export-pdf-btn"><FileText className="h-3.5 w-3.5" /> ملف PDF</button>
              <button onClick={() => { onPrint(); setExportOpen(false); }} className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-xs font-semibold text-slate-200 hover:bg-white/10" data-testid="pitch-print-btn"><Printer className="h-3.5 w-3.5" /> طباعة</button>
            </div>
          )}
        </div>
        {!readOnly && (
          <BarBtn onClick={onSave} disabled={saveStatus === 'saving'} testId="pitch-save-btn" title="حفظ الرسم">
            <Save className={`h-[18px] w-[18px] ${saveStatus === 'saved' ? 'text-emerald-400' : ''}`} />
          </BarBtn>
        )}
      </div>
    </div>
  );
}

/* ---------------- BOTTOM BAR ---------------- */
export function PitchToolbarBottom({
  readOnly, onUndo, onRedo, canUndo, canRedo, color, setColor, strokeWidth, setStrokeWidth,
  snap, setSnap, drawerOpen, setDrawerOpen, statusText,
}) {
  const [colorOpen, setColorOpen] = useState(false);
  if (readOnly) return null;
  return (
    <div className="relative flex items-center justify-between gap-1 border-t border-white/10 bg-[#0B0E14] px-2 py-2" data-testid="pitch-toolbar-bottom">
      <div className="flex items-center gap-1">
        <BarBtn onClick={onUndo} disabled={!canUndo} testId="pitch-undo-btn" title="تراجع"><Undo2 className="h-[18px] w-[18px]" /></BarBtn>
        <BarBtn onClick={onRedo} disabled={!canRedo} testId="pitch-redo-btn" title="إعادة"><Redo2 className="h-[18px] w-[18px]" /></BarBtn>
      </div>
      <span className="hidden text-[11px] text-slate-500 sm:block" data-testid="pitch-status-text">{statusText}</span>
      <div className="flex items-center gap-1">
        <div className="relative">
          <button onClick={() => setColorOpen((o) => !o)} data-testid="pitch-color-toggle-btn" title="لون الرسم"
            className="flex h-10 w-10 items-center justify-center rounded-xl transition-transform active:scale-90 hover:bg-white/10">
            <span className="h-5 w-5 rounded-full ring-2 ring-white/40" style={{ backgroundColor: color }} />
          </button>
          {colorOpen && (
            <div className="absolute bottom-12 right-0 z-30 w-44 space-y-2 rounded-xl border border-white/10 bg-[#151b26] p-2.5 shadow-2xl" data-testid="pitch-color-picker">
              <div className="flex flex-wrap gap-1.5">
                {COLORS.map((c) => (
                  <button key={c} onClick={() => setColor(c)} className={`h-6 w-6 rounded-full border-2 ${color === c ? 'border-emerald-400' : 'border-white/20'}`} style={{ backgroundColor: c }} data-testid={`pitch-color-${c.replace('#', '')}`} />
                ))}
              </div>
              <select value={strokeWidth} onChange={(e) => setStrokeWidth(Number(e.target.value))} className="w-full rounded-lg border border-white/10 bg-[#0B0E14] px-2 py-1.5 text-xs text-slate-200" data-testid="pitch-stroke-width-select">
                <option value={2}>رفيع</option>
                <option value={4}>متوسط</option>
                <option value={7}>عريض</option>
              </select>
            </div>
          )}
        </div>
        <BarBtn onClick={() => setSnap(!snap)} active={snap} testId="pitch-snap-toggle-btn" title="التصاق بالشبكة"><Grid3x3 className="h-[18px] w-[18px]" /></BarBtn>
        <BarBtn onClick={() => setDrawerOpen((o) => !o)} active={drawerOpen} testId="pitch-drawer-toggle-btn" title="أدوات الملعب"><PenTool className="h-[18px] w-[18px]" /></BarBtn>
      </div>
    </div>
  );
}

/* ---------------- SIDE TOOLS DRAWER ---------------- */
export function PitchToolsDrawer({ open, onClose, tool, onInsertToken, onSelectPathTool, hasSelection, onDuplicate, onBringFront, onSendBack, onGroup, onUngroup }) {
  const [activeCategory, setActiveCategory] = useState('team');
  if (!open) return null;
  const items = activeCategory === 'zones'
    ? [...TOKEN_TYPES.filter((t) => t.category === 'zones'), ...PATH_TOOLS.filter((t) => t.category === 'zones')]
    : activeCategory === 'draw'
      ? PATH_TOOLS.filter((t) => t.category === 'draw')
      : TOKEN_TYPES.filter((t) => t.category === activeCategory);
  return (
    <div className="absolute inset-y-0 right-0 z-20 flex w-[172px] flex-col border-l border-white/10 bg-[#101820]/97 backdrop-blur-md sm:w-[200px]" data-testid="pitch-tools-drawer">
      <div className="flex items-center justify-between border-b border-white/10 px-2.5 py-2">
        <span className="text-[11px] font-bold text-slate-300">أدوات الملعب</span>
        <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-white/10" data-testid="pitch-drawer-close-btn"><ChevronLeft className="h-4 w-4" /></button>
      </div>

      <div className="flex gap-1 border-b border-white/10 px-1.5 py-1.5">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setActiveCategory(c.key)}
            title={c.label}
            data-testid={`pitch-category-tab-${c.key}`}
            className={`flex flex-1 flex-col items-center gap-0.5 rounded-lg py-1.5 transition-colors ${activeCategory === c.key ? 'bg-emerald-500 text-white' : 'text-slate-400 hover:bg-white/10'}`}
          >
            <c.icon className="h-4 w-4" />
            <span className="text-[8px] font-bold leading-none">{c.label.split(' ')[0]}</span>
          </button>
        ))}
      </div>

      <div className="grid flex-1 grid-cols-2 gap-1.5 overflow-y-auto p-2 content-start" data-testid={`pitch-tools-grid-${activeCategory}`}>
        {items.map((t) => {
          const ToolIcon = SPORT_ICONS[t.type];
          const isDrag = DRAG_DRAW_TYPES.includes(t.type);
          return (
            <button
              key={t.type}
              onClick={() => (isDrag ? onSelectPathTool(t.type) : onInsertToken(t.type))}
              title={t.label}
              data-testid={`pitch-tool-${t.type}`}
              className={`flex flex-col items-center gap-1 rounded-xl border px-1 py-2 transition-colors ${tool === t.type ? 'border-emerald-400 bg-emerald-500/20 text-emerald-300' : 'border-white/10 text-slate-300 hover:bg-white/10'}`}
            >
              <ToolIcon className="h-5 w-5" />
              <span className="text-center text-[9.5px] font-semibold leading-tight">{t.shortLabel}</span>
            </button>
          );
        })}
      </div>

      {hasSelection && (
        <div className="flex items-center justify-around border-t border-white/10 px-1.5 py-1.5">
          <BarBtn onClick={onDuplicate} testId="pitch-duplicate-btn" title="تكرار"><Copy className="h-4 w-4" /></BarBtn>
          <BarBtn onClick={onBringFront} testId="pitch-front-btn" title="للأمام"><Layers className="h-4 w-4" /></BarBtn>
          <BarBtn onClick={onSendBack} testId="pitch-back-btn" title="للخلف"><Layers className="h-4 w-4 opacity-50" /></BarBtn>
          <BarBtn onClick={onGroup} testId="pitch-group-btn" title="تجميع"><Users className="h-4 w-4" /></BarBtn>
          <BarBtn onClick={onUngroup} testId="pitch-ungroup-btn" title="فك التجميع"><MoreHorizontal className="h-4 w-4" /></BarBtn>
        </div>
      )}
    </div>
  );
}

/* ---------------- SETTINGS SHEET ---------------- */
export function PitchSettingsSheet({
  open, onClose, fieldTemplate, setFieldTemplate, orientation, setOrientation,
  fieldBackground, setFieldBackground, teamColors, setTeamColors, showNames, setShowNames,
}) {
  if (!open) return null;
  const templates = [
    { key: 'full', label: 'ملعب كامل' }, { key: 'half', label: 'نصف ملعب' }, { key: 'third', label: 'ثلث ملعب' },
    { key: 'penalty_area', label: 'منطقة الجزاء' }, { key: 'blank', label: 'مساحة فارغة' },
  ];
  return (
    <div className="absolute inset-0 z-30 flex flex-col justify-end bg-black/50" data-testid="pitch-settings-sheet" onClick={onClose}>
      <div className="max-h-[80%] overflow-y-auto rounded-t-2xl bg-[#101820] p-4 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-white">إعدادات الملعب</span>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="pitch-settings-close-btn"><X className="h-5 w-5" /></button>
        </div>

        <div>
          <div className="mb-1.5 text-[11px] font-semibold text-slate-400">نوع الملعب</div>
          <div className="flex flex-wrap gap-1.5">
            {templates.map((t) => (
              <button key={t.key} onClick={() => setFieldTemplate(t.key)} data-testid={`pitch-field-template-${t.key}`}
                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${fieldTemplate === t.key ? 'border-emerald-400 bg-emerald-500/15 text-emerald-300' : 'border-white/10 text-slate-300'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-1.5 text-[11px] font-semibold text-slate-400">الاتجاه</div>
          <div className="flex gap-1.5">
            <button onClick={() => setOrientation('vertical')} data-testid="pitch-orientation-vertical" className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${orientation === 'vertical' ? 'border-emerald-400 bg-emerald-500/15 text-emerald-300' : 'border-white/10 text-slate-300'}`}>طولي</button>
            <button onClick={() => setOrientation('horizontal')} data-testid="pitch-orientation-horizontal" className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${orientation === 'horizontal' ? 'border-emerald-400 bg-emerald-500/15 text-emerald-300' : 'border-white/10 text-slate-300'}`}>عرضي</button>
          </div>
        </div>

        <div>
          <div className="mb-1.5 text-[11px] font-semibold text-slate-400">خلفية الملعب</div>
          <div className="flex flex-wrap gap-2">
            {FIELD_BACKGROUNDS.map((b) => (
              <button key={b.key} onClick={() => setFieldBackground(b.key)} title={b.label} data-testid={`pitch-bg-${b.key}`}
                className={`h-9 w-9 rounded-full border-2 ${fieldBackground === b.key ? 'border-emerald-400' : 'border-white/20'}`} style={{ backgroundColor: b.fill }} />
            ))}
          </div>
        </div>

        <div>
          <div className="mb-1.5 text-[11px] font-semibold text-slate-400">ألوان الفرق</div>
          <div className="flex flex-wrap items-center gap-3">
            <TeamColorPicker label="فريق 1" value={teamColors.a} onChange={(c) => setTeamColors((t) => ({ ...t, a: c }))} testId="pitch-team-a-color" />
            <TeamColorPicker label="فريق 2" value={teamColors.b} onChange={(c) => setTeamColors((t) => ({ ...t, b: c }))} testId="pitch-team-b-color" />
          </div>
          <label className="mt-2 flex items-center gap-1.5 text-xs text-slate-300">
            <input type="checkbox" checked={showNames} onChange={(e) => setShowNames(e.target.checked)} data-testid="pitch-show-names-toggle" />
            إظهار أسماء اللاعبين
          </label>
        </div>
      </div>
    </div>
  );
}

const TeamColorPicker = ({ label, value, onChange, testId }) => (
  <div className="flex items-center gap-1.5 text-xs text-slate-300">
    <span>{label}</span>
    <div className="flex items-center gap-1">
      {['#2563eb', '#dc2626', '#16a34a', '#f97316', '#7c3aed', '#0f172a'].map((c) => (
        <button key={c} onClick={() => onChange(c)} className={`h-5 w-5 rounded-full border-2 ${value === c ? 'border-emerald-400' : 'border-white/20'}`} style={{ backgroundColor: c }} data-testid={`${testId}-${c.replace('#', '')}`} />
      ))}
    </div>
  </div>
);
